import Hyperbeam from '@hyperbeam/web';
import { print } from 'graphql';

import { REMOVE_HYPERBEAM_SESSION, UtSessionSyncType } from '@/survey-graphql';

import type { HyperbeamSessionGateway } from './shared-survey-hyperbeam-session.gateway';
import {
  connectMobileUtAudioProcessor,
  connectMobileUtHyperbeamAudio,
  createMobileUtAudioProcessor,
  createMobileUtMicMixer,
  startMobileUtAudioRecorder,
} from './shared-survey-mobile-ut-phone-audio.helpers';
import { createMobileUtChunkUploader } from './shared-survey-mobile-ut-phone-upload.helpers';
import {
  attachMobileUtWorkerListeners,
  postMobileUtStartListening,
  requestMobileUtAssemblyToken,
} from './shared-survey-mobile-ut-phone-worker.helpers';
import type {
  HyperbeamInstance,
  MobileUtSessionMutations,
} from './shared-survey-mobile-ut-phone.types';
import { shouldSendTranscriptUpdate } from './shared-survey-mobile-ut.helpers';
import { createSpeechToTextWorker } from './shared-survey-speech-to-text-worker';

interface MobileUtSessionOptions {
  uniqueId: string;
  mutations: MobileUtSessionMutations;
  hyperbeamGateway: HyperbeamSessionGateway;
  onVoiceFailure: () => void;
}

interface MobileUtSessionStartInput {
  container: HTMLDivElement;
  usesMicrophone: boolean;
  startUrl: string;
  width: number;
  height: number;
}

// sendBeacon-based cleanup for page unload — Apollo mutation may not finish before tab dies on mobile.
function removeHyperbeamSessionViaBeacon(hbSid: string) {
  const payload = JSON.stringify({
    query: print(REMOVE_HYPERBEAM_SESSION),
    variables: { input: { sessionId: hbSid } },
  });
  const blob = new Blob([payload], { type: 'application/json' });
  navigator.sendBeacon('/survey/graphql', blob);
}

export type MobileUtSession = ReturnType<typeof createMobileUtSession>;

export function createMobileUtSession({
  uniqueId,
  mutations,
  hyperbeamGateway,
  onVoiceFailure,
}: MobileUtSessionOptions) {
  let isDisposed = false;
  let isEnded = false;
  let hyperbeamSessionId: string | null = null;
  let lastTranscriptUpdateAt: number | null = null;
  let uploadMimeType = 'audio/webm';
  let chunkIndex = 0;

  let hyperbeam: HyperbeamInstance | null = null;
  let hyperbeamAudioTrack: MediaStreamTrack | null = null;
  let hyperbeamAudioSource: MediaStreamAudioSourceNode | null = null;
  let micStream: MediaStream | null = null;
  let audioContext: AudioContext | null = null;
  let masterDestination: MediaStreamAudioDestinationNode | null = null;
  let micMixerSource: MediaStreamAudioSourceNode | null = null;
  let recorder: MediaRecorder | null = null;
  let worker: Worker | null = null;
  let processor: AudioWorkletNode | ScriptProcessorNode | null = null;
  let processorSource: MediaStreamAudioSourceNode | null = null;
  let processorCleanup: (() => void) | null = null;

  const { enqueueChunkUpload } = createMobileUtChunkUploader({
    uniqueId,
    createUploadUrl: mutations.createUploadUrl,
    getMimeType: () => uploadMimeType,
  });

  function wireHyperbeamAudioIfReady() {
    if (!audioContext || !masterDestination || !hyperbeamAudioTrack) return;
    if (hyperbeamAudioSource) return;

    hyperbeamAudioSource = connectMobileUtHyperbeamAudio(
      audioContext,
      masterDestination,
      hyperbeamAudioTrack
    );
  }

  function handleRecordedChunk(blob: Blob) {
    if (isDisposed) return;
    const index = chunkIndex;
    chunkIndex += 1;
    enqueueChunkUpload(blob, index);
  }

  function claimTranscriptUpdate(isFinal: boolean) {
    const now = Date.now();
    if (!shouldSendTranscriptUpdate(isFinal, lastTranscriptUpdateAt, now)) {
      return false;
    }
    lastTranscriptUpdateAt = now;
    return true;
  }

  function stop() {
    if (processorCleanup) {
      processorCleanup();
      processorCleanup = null;
    }
    if (processorSource) {
      processorSource.disconnect();
      processorSource = null;
    }
    if (processor) {
      processor.disconnect();
      processor = null;
    }
    if (hyperbeamAudioSource) {
      hyperbeamAudioSource.disconnect();
      hyperbeamAudioSource = null;
    }
    if (micMixerSource) {
      micMixerSource.disconnect();
      micMixerSource = null;
    }
    if (masterDestination) {
      masterDestination.disconnect();
      masterDestination = null;
    }
    hyperbeamAudioTrack = null;
    if (audioContext) {
      void audioContext.close();
      audioContext = null;
    }
    if (micStream) {
      micStream.getTracks().forEach((track) => track.stop());
      micStream = null;
    }
    if (worker) {
      worker.terminate();
      worker = null;
    }
    if (recorder) {
      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
      recorder = null;
    }
    if (hyperbeam) {
      hyperbeam.destroy();
      hyperbeam = null;
    }
  }

  function takeHyperbeamSessionId() {
    const id = hyperbeamSessionId;
    hyperbeamSessionId = null;
    return id;
  }

  function removeHyperbeamSession(id: string | null) {
    if (!id) return;
    hyperbeamGateway.remove(id).catch(() => undefined);
  }

  function handleVoiceFailure() {
    onVoiceFailure();
    stop();
  }

  async function startVoicePipeline(stream: MediaStream) {
    const context = new AudioContext();
    audioContext = context;
    if (context.state === 'suspended') {
      await context.resume().catch(() => undefined);
    }

    const mixer = createMobileUtMicMixer(context, stream);
    masterDestination = mixer.masterDestination;
    micMixerSource = mixer.micSource;
    wireHyperbeamAudioIfReady();

    chunkIndex = 0;
    const recording = startMobileUtAudioRecorder(
      mixer.masterDestination,
      handleRecordedChunk
    );
    recorder = recording.recorder;
    uploadMimeType = recording.mimeType;

    mutations
      .syncTimestamp({
        variables: {
          input: { uniqueId, type: UtSessionSyncType.AudioStarted },
        },
      })
      .catch(() => undefined);

    const voiceWorker = await createSpeechToTextWorker();
    worker = voiceWorker;
    if (isDisposed) return false;

    const { data: tokenData } = await requestMobileUtAssemblyToken(
      mutations.getAssemblyAIToken
    );
    if (!tokenData?.assemblyAIToken?.token) {
      throw new Error('Failed to get AssemblyAI token');
    }
    if (isDisposed) return false;

    const sampleRate = context.sampleRate;
    const audioProcessor = await createMobileUtAudioProcessor(
      context,
      () => worker
    );
    processorCleanup = audioProcessor.cleanup;
    processor = audioProcessor.processor;
    processorSource = connectMobileUtAudioProcessor(
      context,
      stream,
      audioProcessor.processor
    );

    postMobileUtStartListening(
      voiceWorker,
      tokenData.assemblyAIToken,
      sampleRate
    );
    attachMobileUtWorkerListeners(voiceWorker, {
      uniqueId,
      submitTranscript: mutations.submitTranscript,
      getAssemblyAIToken: mutations.getAssemblyAIToken,
      getWorker: () => worker,
      claimTranscriptUpdate,
      onVoiceFailure: handleVoiceFailure,
    });

    return !isDisposed;
  }

  async function runStart({
    container,
    usesMicrophone,
    startUrl,
    width,
    height,
  }: MobileUtSessionStartInput) {
    const created = await hyperbeamGateway.create({ startUrl, width, height });
    hyperbeamSessionId = created.sessionId;
    if (isDisposed) return false;

    hyperbeam = await Hyperbeam(container, created.embedUrl, {
      adminToken: created.adminToken || undefined,
      ...(usesMicrophone && {
        audioTrackCb: (track: MediaStreamTrack) => {
          hyperbeamAudioTrack = track;
          wireHyperbeamAudioIfReady();
        },
      }),
    });
    if (isDisposed) return false;
    if (!usesMicrophone) return true;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });
    micStream = stream;
    if (isDisposed) return false;

    return startVoicePipeline(stream);
  }

  async function start(input: MobileUtSessionStartInput): Promise<boolean> {
    try {
      const isLive = await runStart(input);
      if (!isLive) stop();
      return isLive;
    } catch (error) {
      stop();
      throw error;
    }
  }

  function end(): boolean {
    if (isEnded) return false;
    isEnded = true;
    const id = takeHyperbeamSessionId();
    stop();
    removeHyperbeamSession(id);
    return true;
  }

  function handlePageHide() {
    if (recorder?.state === 'recording') {
      recorder.stop();
    }
    const id = takeHyperbeamSessionId();
    if (id) removeHyperbeamSessionViaBeacon(id);
  }

  function resumeSuspendedAudio() {
    if (audioContext?.state === 'suspended') {
      audioContext.resume().catch(() => undefined);
    }
  }

  function dispose() {
    if (isDisposed) return;
    isDisposed = true;
    const id = takeHyperbeamSessionId();
    stop();
    removeHyperbeamSession(id);
  }

  return { start, end, handlePageHide, resumeSuspendedAudio, dispose };
}
